import { Helmet } from "react-helmet-async";
import {
  Box,
  Checkbox,
  Divider,
  Flex,
  Input,
  Spacer,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import AppIconButton from "@/features/line-sheet-sets/app-icon-button";
import AppButton from "@/features/line-sheet-sets/app-button";
import AppTabSelect from "@/features/line-sheet-sets/app-tap-select";
import SVG from "react-inlinesvg";
import SheetInfoPanel from "@/features/line-sheet-sets/sheet-info-panel";
import SheetTabFilter, {
  EMPTY_SHEET_TAB_FILTER_QUERY,
  isProductFiltered,
  SheetTabFilterQuery,
} from "@/features/order-sheet-sets/sheet-tab-filter";
import { useParams } from "react-router";
import useIdentity from "@/features/ui/hooks/use-identity";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import LineSheetNotFoundPage from "@/features/line-sheet-sets/line-sheet-not-found-page";
import { isLineSheetSetUnavailableError } from "@/features/line-sheet-sets/line-sheet-set-error";
import useLineSheetSetPagePersistedQuery from "@/features/line-sheet-sets/hooks/use-line-sheet-set-page-persisted-query";
import LineSheetSetBillboard from "@/features/line-sheet-sets/line-sheet-set-billboard";
import { produce } from "immer";
import LineSheetSelector from "@/features/line-sheet-sets/line-sheet-selector";
import {
  LightLineSheet,
  LightLineSheetTab,
} from "@/features/line-sheet-sets/line-sheet-set.types";
import useFetchAndSetLineSheetTabs, {
  UseFetchAndSetLineSheetTabsParameter,
} from "@/features/line-sheet-sets/use-fetch-and-set-line-sheet-tabs";
import LineSheetTab from "@/features/line-sheet-sets/line-sheet-tab";
import { ColumnApi, GridApi, GridReadyEvent } from "ag-grid-community";
import useInflatedTabs from "@/features/line-sheet-sets/helpers/use-inflated-tabs";
import {
  FlatProduct,
  inflatedTabsFamily,
  orderSheetHasDiscountConditionFamily,
} from "@/features/line-sheet-sets/helpers/sheet-state";
import useUpdateLineSheetTab from "@/features/line-sheet-sets/hooks/use-update-line-sheet-tab";
import PriceAdjustedByTypeSelect from "@/features/order-sheet-sets/price-adjusted-by-type-select";
import { PriceAdjustedBy } from "@/features/line-sheet-sets/line-sheet-set.types";
import { UpdateLineSheetSetOrderRulesResponse } from "@/features/line-sheet-sets/hooks/use-update-line-sheet-set-order-rules";
import useWrappedNavigate from "@/features/ui/hooks/use-wrapped-navigate";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import AppDownloadButton from "@/features/invoices/app-download-button";
import usePublishLineSheetSetIfConfirmed from "@/features/line-sheet-sets/hooks/use-publish-line-sheet-set-if-confirmed";
import useFetchAndSetLineSheetSet, {
  UseFetchAndSetLineSheetSetParameter,
} from "./hooks/use-fetch-and-set-line-sheet-set";
import useListAllCompanies, {
  ListAllCompaniesPathParameter,
  ListAllCompaniesRequest,
} from "@/features/line-sheet-sets/hooks/use-list-all-companies";
import useCleanLineSheetImage from "@/features/line-sheet-sets/hooks/use-clean-line-sheet-image";
import useNormalizeLineSheet from "@/features/line-sheet-sets/hooks/use-normalize-line-sheet";
import useAppDialog from "@/features/ui/hooks/use-app-dialog";
import { useLineSheetSetDumbTransaction } from "@/features/line-sheet-sets/hooks/use-line-sheet-set-dumb-transaction";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import AppSelect from "@/features/line-sheet-sets/app-select";
import { useRecoilValue } from "recoil";
import { getCanonicalUrl } from "@/shared/utils/canonical-url";

function openPdfWindow(pdfUrl: string) {
  const w = 1200;
  const h = 900;

  window.open(
    pdfUrl,
    "_blank",
    "popup=yes,width=" +
      w +
      ",height=" +
      h +
      ",resizable=yes,scrollbars=yes,location=no,toolbar=no,menubar=no,status=no"
  );
}

function openImageWindow(imageUrl: string) {
  const w = 1200;
  const h = 900;

  const newWindow = window.open(
    "",
    "_blank",
    "popup=yes,width=" +
      w +
      ",height=" +
      h +
      ",resizable=yes,scrollbars=yes,location=no,toolbar=no,menubar=no,status=no"
  );

  newWindow?.document.write(`
    <html>
      <head>
        <title>Image Viewer</title>
        <style>
          * {
            box-sizing: border-box;
          }
          body {
            margin: 0;
            background: #111;
            color: #fff;
            font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
            display: flex;
            flex-direction: column;
            height: 100vh;
            user-select: none;      /* 텍스트/이미지 선택 방지 */
          }
          .toolbar {
            padding: 8px 12px;
            background: #222;
            border-bottom: 1px solid #333;
            display: flex;
            gap: 8px;
            align-items: center;
          }
          .toolbar button {
            padding: 4px 10px;
            border-radius: 4px;
            border: 1px solid #555;
            background: #333;
            color: #fff;
            font-size: 13px;
            cursor: pointer;
          }
          .toolbar button:hover {
            background: #444;
          }
          .zoom-label {
            margin-left: 8px;
            font-size: 12px;
            color: #aaa;
          }
          .viewer {
            flex: 1;
            overflow: auto;      /* 확대 시 가로/세로 스크롤 */
            background: #111;
            cursor: grab;
          }
          .viewer.dragging {
            cursor: grabbing;
          }
          .image-wrapper {
            padding: 8px;
            display: block;      /* 왼쪽 정렬 */
          }
          img {
            display: block;
            width: 100%;         /* 기본 100% = 창 너비 기준 */
            height: auto;
          }
        </style>
      </head>
      <body>
        <div class="toolbar">
          <button id="zoomIn">+</button>
          <button id="zoomOut">-</button>
          <button id="resetZoom">Reset</button>
          <span class="zoom-label" id="zoomLabel">100%</span>
        </div>
        <div class="viewer" id="viewer">
          <div class="image-wrapper">
            <img id="img" src="${imageUrl}" draggable="false" />
          </div>
        </div>

        <script>
          (function() {
            var img = document.getElementById('img');
            var zoomLabel = document.getElementById('zoomLabel');
            var viewer = document.getElementById('viewer');

            var scale = 1;
            var minScale = 0.25;
            var maxScale = 4;
            var step = 0.25;

            function applyZoom(resetScroll) {
              var percent = Math.round(scale * 100);
              zoomLabel.textContent = percent + '%';
              img.style.width = (scale * 100) + '%';

              if (resetScroll) {
                viewer.scrollLeft = 0;
                viewer.scrollTop = 0;
              }
            }

            document.getElementById('zoomIn').addEventListener('click', function() {
              scale = Math.min(maxScale, scale + step);
              applyZoom(false);
            });

            document.getElementById('zoomOut').addEventListener('click', function() {
              scale = Math.max(minScale, scale - step);
              applyZoom(false);
            });

            document.getElementById('resetZoom').addEventListener('click', function() {
              scale = 1;
              applyZoom(true);
            });

            // ===== 드래그로 스크롤 이동 =====
            var isDragging = false;
            var startX, startY, startScrollLeft, startScrollTop;

            // mousedown은 viewer에서만 시작
            viewer.addEventListener('mousedown', function(e) {
              // 왼쪽 버튼만
              if (e.button !== 0) return;
              isDragging = true;
              viewer.classList.add('dragging');
              startX = e.clientX;
              startY = e.clientY;
              startScrollLeft = viewer.scrollLeft;
              startScrollTop = viewer.scrollTop;
            });

            // mousemove / mouseup 은 창 전체에서 감지
            newWindow.addEventListener('mousemove', function(e) {
              if (!isDragging) return;
              e.preventDefault(); // 텍스트 선택/이미지 드래그 방지
              var dx = e.clientX - startX;
              var dy = e.clientY - startY;
              viewer.scrollLeft = startScrollLeft - dx;
              viewer.scrollTop = startScrollTop - dy;
            });

            newWindow.addEventListener('mouseup', function() {
              if (!isDragging) return;
              isDragging = false;
              viewer.classList.remove('dragging');
            });

            newWindow.addEventListener('blur', function() {
              // 창 포커스 잃으면 드래그 강제 종료
              isDragging = false;
              viewer.classList.remove('dragging');
            });

            applyZoom(true);
          })();
        </script>
      </body>
    </html>
  `);
}

export default function LineSheetSetPage() {
  const navigate = useWrappedNavigate();
  const { navigate: navigateHelper } = useRouterHelper();

  const { openDialog } = useAppDialog();
  const identity = useIdentity();
  const company = useMemo(() => {
    return identity?.company;
  }, [identity]);
  const { error: showError, warning: showWarning } = useAppToasts();
  const { lineSheetSetId: rawLineSheetSetId } = useParams();
  const lineSheetSetId = useMemo(() => {
    return rawLineSheetSetId ? parseInt(rawLineSheetSetId) || -1 : -1;
  }, [rawLineSheetSetId]);

  const [queryCount, setQueryCount] = useState(0);

  const { fire: publishLineSheetSetIfConfirmed } =
    usePublishLineSheetSetIfConfirmed();

  const { fire: cleanLineSheetImage } = useCleanLineSheetImage();
  const { fire: normalizeLineSheet } = useNormalizeLineSheet();

  useEffect(() => {
    if (lineSheetSetId === -1) {
      navigate("/");
    }
  }, [lineSheetSetId, navigate]);
  const { t, tTitle } = useI18nHelper();

  const getLineSheetSetParameter =
    useMemo((): UseFetchAndSetLineSheetSetParameter => {
      return {
        data: {
          lineSheetSetId: lineSheetSetId,
        },
        // 삭제/미발행 등 접근 불가 라인시트는 아래에서 안내 페이지로 렌더하므로 토스트를 띄우지 않는다.
        suppressUnavailableToast: true,
      };
    }, [lineSheetSetId]);

  const { getLineSheetSetError, getLineSheetSetData, mutateGetLineSheetSet } =
    useFetchAndSetLineSheetSet(getLineSheetSetParameter);

  useEffect(() => {
    // 존재하지 않는/접근 불가 라인시트(404/403)는 아래에서 안내 페이지로 렌더하므로 토스트를 띄우지 않는다.
    if (
      getLineSheetSetError &&
      !isLineSheetSetUnavailableError(getLineSheetSetError)
    ) {
      showError(getLineSheetSetError);
    }
  }, [showError, getLineSheetSetError]);

  const { value: query, set: setQuery } = useLineSheetSetPagePersistedQuery();

  const lineSheetSet = useMemo(() => {
    return getLineSheetSetData;
  }, [getLineSheetSetData]);

  const selectedLineSheet = useMemo(() => {
    if (lineSheetSet && query.lineSheetId) {
      return lineSheetSet.lineSheets.find(
        (lineSheet) => lineSheet.id === query.lineSheetId
      );
    }
  }, [lineSheetSet, query.lineSheetId]);

  const lineSheetSelectValues = useMemo(() => {
    if (lineSheetSet) {
      return lineSheetSet.lineSheets.filter(
        (lineSheet) => lineSheet.status === "PARSED"
      );
    }
    return [];
  }, [lineSheetSet]);

  const lineSheetTabSelectValues = useMemo(() => {
    if (lineSheetSet && selectedLineSheet) {
      return selectedLineSheet.lineSheetTabs;
    }
    return [];
  }, [lineSheetSet, selectedLineSheet]);

  const selectedLineSheetTab = useMemo(() => {
    if (
      lineSheetSet &&
      selectedLineSheet &&
      query.lineSheetTabIndex !== undefined
    ) {
      const tabIndex = query.lineSheetTabIndex;
      return selectedLineSheet.lineSheetTabs.find(
        (tab) => tab.index === tabIndex
      );
    }
  }, [lineSheetSet, selectedLineSheet, query.lineSheetTabIndex]);

  //기본 tabIndex 채워주기
  useEffect(() => {
    if (
      lineSheetSet &&
      selectedLineSheet &&
      query.lineSheetTabIndex === undefined
    ) {
      setQuery(
        (prev) => {
          return {
            ...prev,
            lineSheetTabIndex:
              selectedLineSheet.lineSheetTabs.length > 0
                ? selectedLineSheet.lineSheetTabs[0].index
                : undefined,
          };
        },
        {
          replace: true,
        }
      );
    }
  }, [lineSheetSet, selectedLineSheet, query.lineSheetTabIndex, setQuery]);

  const handleSelectLineSheet = useCallback(
    (value?: LightLineSheet) => {
      if (value) {
        setQuery(
          (prev) => {
            return {
              ...prev,
              lineSheetId: value.id,
              // lineSheetTabIndex는 유지 — 같은 인덱스 탭이 새 시트에 있으면 그 탭으로,
              // 없으면 자동 폴백 로직(있다면)이 처리.
              ...EMPTY_SHEET_TAB_FILTER_QUERY,
            };
          },
          {
            replace: true,
          }
        );
      }
    },
    [setQuery]
  );

  const handleSelectLineSheetTab = useCallback(
    (value?: LightLineSheetTab) => {
      if (value) {
        setQuery(
          (prev) => {
            return {
              ...prev,
              lineSheetTabIndex: value.index,
              ...EMPTY_SHEET_TAB_FILTER_QUERY,
              query: prev.query,
            };
          },
          {
            replace: true,
          }
        );
      }
    },
    [setQuery]
  );

  const [isOrderRulesPanelOpen, setIsOrderRulesPanelOpen] = useState<boolean>(
    localStorage.getItem("isSheetInfoOpen") === "true"
  );

  const [isSheetInfoButtonHovered, setIsSheetInfoButtonHovered] =
    useState<boolean>(false);

  const handleOrderRulesToggle = useCallback(() => {
    setIsOrderRulesPanelOpen((prev) => !prev);
    localStorage.setItem("isSheetInfoOpen", String(!isOrderRulesPanelOpen));
  }, [isOrderRulesPanelOpen]);

  const handleOrderRulesSave = useCallback(
    (response: UpdateLineSheetSetOrderRulesResponse) => {
      mutateGetLineSheetSet(
        (prev) => {
          if (prev) {
            prev.orderRules = [];
            return produce(prev, (draft) => {
              draft.orderRules = response.orderRules;
            });
          }
        },
        { revalidate: false }
      );
    },
    [mutateGetLineSheetSet]
  );

  const useFetchAndSetLineSheetTabsParameter =
    useMemo((): UseFetchAndSetLineSheetTabsParameter => {
      if (lineSheetSet && selectedLineSheet) {
        return {
          data: {
            lineSheetSetId: lineSheetSet.id,
            lineSheetId: selectedLineSheet.id,
            tabIndexes: selectedLineSheet.lineSheetTabs.map((i) => i.index),
          },
          query,
        };
      }
      return {
        data: undefined,
      };
    }, [lineSheetSet, selectedLineSheet, query]);

  const { mutateListAllLineSheetTabs } = useFetchAndSetLineSheetTabs(
    useFetchAndSetLineSheetTabsParameter
  );

  const gridRef = useRef<{
    api: GridApi<FlatProduct>;
    columnApi: ColumnApi;
  } | null>(null);

  const handleGridReady = useCallback((event: GridReadyEvent<FlatProduct>) => {
    gridRef.current = {
      api: event.api,
      columnApi: event.columnApi,
    };
  }, []);

  const [selectedProductIds, setSelectedProductIds] = useState<number[]>([]);

  const { fire: fireUpdateLineSheetTab, isLoading: isSaving } =
    useUpdateLineSheetTab();

  const handleFilterSelect = useCallback(
    (query: SheetTabFilterQuery) => {
      setQuery(
        (prev) => {
          return {
            ...prev,
            ...query,
          };
        },
        {
          replace: true,
        }
      );
    },
    [setQuery]
  );

  const _selectedLineSheetId = useMemo(() => {
    return selectedLineSheet?.id || -1;
  }, [selectedLineSheet]);

  const brandKey = useMemo(():
    | {
        parameter: ListAllCompaniesPathParameter;
        request: ListAllCompaniesRequest;
      }
    | undefined => {
    if (identity?.company) {
      let request: ListAllCompaniesRequest = {};
      request.type__in = ["BRAND"];
      return {
        parameter: {
          by: identity.company.type,
          companyId: identity.company.id,
        },
        request,
      };
    }
    return undefined;
  }, [identity]);

  const { data: companiesData } = useListAllCompanies(
    brandKey?.parameter,
    brandKey?.request
  );

  useEffect(() => {
    const madeByCompanyIds = query["madeByCompany.id__in"];
    if (madeByCompanyIds && madeByCompanyIds.length > 0) {
      setQuery(
        (prev) => {
          return {
            ...prev,
            "madeByCompany.id__in": undefined,
            // 상품 brandName이 표준명(company.name) / 원본명(company.orgName) 둘 중
            // 어느 쪽으로도 저장될 수 있어 이름 매칭은 불안정. 브랜드 회사 id로 매칭.
            brandId__in: madeByCompanyIds,
          };
        },
        {
          replace: true,
        }
      );
    }
  }, [query, setQuery]);

  // 진입 시 URL의 brand_id__in에 "undefined"/"NaN"/"" 같은 stale 값이 박혀 있으면 한 번에 정리.
  // (이전 버그로 박힌 URL 잔존 / 외부에서 들어온 dirty URL 대비)
  const cleanedStaleUrlRef = useRef(false);
  useEffect(() => {
    if (cleanedStaleUrlRef.current) return;
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const values = params.getAll("brand_id__in");
    const hasInvalid = values.some(
      (v) => v === "undefined" || v === "NaN" || v === ""
    );
    if (hasInvalid) {
      // setQuery 라운드트립을 한 번 돌리면 custom stringify + transformer 수정 덕에 깨끗해짐.
      setQuery((prev) => ({ ...prev }), { replace: true });
    }
    cleanedStaleUrlRef.current = true;
  }, [setQuery]);

  // 시트 이동 시 브랜드 필터 자동 해제.
  // (목록 → 진입 직후의 최초 set은 이전 id가 없는 상태라 클리어하지 않음.)
  const prevLineSheetIdRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    const currentId = selectedLineSheet?.id;
    if (
      prevLineSheetIdRef.current !== undefined &&
      currentId !== undefined &&
      prevLineSheetIdRef.current !== currentId
    ) {
      setQuery((prev) => ({ ...prev, brandId__in: undefined }), {
        replace: true,
      });
    }
    prevLineSheetIdRef.current = currentId;
  }, [selectedLineSheet?.id, setQuery]);

  const {
    getInflatedProductById,
    updateInflatedProducts,
    filterInflatedProducts,
    resetInflatedSheet,
  } = useInflatedTabs();

  useEffect(() => {
    if (selectedLineSheet && selectedLineSheetTab) {
      filterInflatedProducts(
        {
          lineSheetSetId,
          lineSheetId: selectedLineSheet.id,
        },
        selectedLineSheetTab.index,
        (product) => {
          return isProductFiltered(product, query);
        },
        query.orderBy || "",
        false
      );
    }
  }, [
    lineSheetSetId,
    selectedLineSheet,
    selectedLineSheetTab,
    filterInflatedProducts,
    query,
  ]);

  const sheetKey = useMemo(() => {
    return {
      lineSheetSetId,
      lineSheetId: _selectedLineSheetId,
    };
  }, [lineSheetSetId, _selectedLineSheetId]);

  // --- priceAdjustedBy 저장 / 일괄 컨디션 적용 ---
  const inflatedTabsState = useRecoilValue(inflatedTabsFamily(sheetKey));

  // 페이지 최초 진입 시 현재 탭에 검색 결과가 없으면 결과가 있는 다른 탭으로 1회만 자동 이동
  const hasAutoSwitchedTabRef = useRef(false);
  useEffect(() => {
    if (hasAutoSwitchedTabRef.current) return;
    if (!selectedLineSheet || !selectedLineSheetTab) return;
    if (!inflatedTabsState) return;

    // 데이터가 처음 모두 준비된 시점 — 결과와 무관하게 한 번만 평가
    hasAutoSwitchedTabRef.current = true;

    if (!query.query || query.query.trim() === "") return;

    const currentTab = inflatedTabsState.tabs.find(
      (t) => t.index === selectedLineSheetTab.index
    );
    if (!currentTab) return;
    const currentHasMatches = currentTab.products.some((p) =>
      isProductFiltered(p, query)
    );
    if (currentHasMatches) return;

    const targetTab = inflatedTabsState.tabs.find(
      (t) =>
        t.index !== selectedLineSheetTab.index &&
        t.products.some((p) => isProductFiltered(p, query))
    );
    if (!targetTab) return;

    setQuery(
      (prev) => ({
        ...prev,
        lineSheetTabIndex: targetTab.index,
      }),
      { replace: true }
    );
  }, [
    selectedLineSheet,
    selectedLineSheetTab,
    query,
    inflatedTabsState,
    setQuery,
  ]);

  const [isDirty, setIsDirty] = useState(false);

  // tab이나 lineSheet가 바뀌면 dirty 초기화
  useEffect(() => {
    setIsDirty(false);
  }, [sheetKey, selectedLineSheetTab]);

  const handleSave = useCallback(async () => {
    if (
      !identity?.company ||
      !selectedLineSheet ||
      selectedLineSheetTab === undefined
    )
      return;

    const tab = inflatedTabsState?.tabs.find(
      (t) => t.index === selectedLineSheetTab.index
    );
    if (!tab) return;

    const dirtyProducts = tab.products.filter((p) => p.isDirty);
    if (dirtyProducts.length === 0) return;

    try {
      await fireUpdateLineSheetTab(
        {
          by: identity.company.type,
          companyId: identity.company.id,
          lineSheetSetId,
          lineSheetId: selectedLineSheet.id,
          lineSheetTabIndex: selectedLineSheetTab.index,
        },
        {
          products: dirtyProducts.map((p) => ({
            id: p.id,
            priceAdjustedBy: p.priceAdjustedBy,
          })),
        }
      );
      setIsDirty(false);
      mutateListAllLineSheetTabs();
    } catch (e) {
      showError(e);
    }
  }, [
    identity,
    selectedLineSheet,
    selectedLineSheetTab,
    inflatedTabsState,
    lineSheetSetId,
    fireUpdateLineSheetTab,
    mutateListAllLineSheetTabs,
    showError,
  ]);

  // --- 일괄 컨디션 적용 ---
  const [bulkBy, setBulkBy] = useState<PriceAdjustedBy["by"]>("MARKUP");
  const [bulkAmount, setBulkAmount] = useState<string>("0");

  const handleBulkApply = useCallback(() => {
    const bulkAmountNum = parseFloat(bulkAmount);
    if (isNaN(bulkAmountNum) || bulkAmountNum > 100 || bulkAmountNum < -100) {
      showWarning(
        t("common:apply_price_condition_form.messages.error.out_of_range"),
        "apply_price_condition_form"
      );
      return;
    }
    if (!selectedLineSheetTab) return;

    const selectedProducts = selectedProductIds
      .map((id) =>
        getInflatedProductById(sheetKey, selectedLineSheetTab.index, id)
      )
      .filter((p): p is NonNullable<typeof p> => p !== undefined);

    const nextProducts = produce(selectedProducts, (draft) => {
      for (const product of draft) {
        // by/value만 갱신, 기존 applyIva는 보존 (없으면 null).
        product.priceAdjustedBy = {
          by: bulkBy,
          value: bulkAmountNum,
          applyIva: product.priceAdjustedBy?.applyIva ?? null,
        };
      }
    });
    updateInflatedProducts(sheetKey, selectedLineSheetTab.index, nextProducts);
    setIsDirty(true);
  }, [
    bulkBy,
    bulkAmount,
    selectedProductIds,
    sheetKey,
    selectedLineSheetTab,
    getInflatedProductById,
    updateInflatedProducts,
    showWarning,
    t,
  ]);

  // --- 일괄 IVA 적용 ---
  // 주문서의 OrderSheetAdjustPriceFragment 와 같은 동작 — 컨디션(by/value)은 그대로 두고
  // applyIva/ivaValue 만 바꾼다. IVA 는 DISCOUNT 에서만 의미가 있어 그 상품이 하나도 없으면 감춘다.
  const [bulkApplyIva, setBulkApplyIva] = useState<boolean>(true);
  const [bulkIvaValue, setBulkIvaValue] = useState<string>("/1.22");
  const hasDiscountCondition = useRecoilValue(
    orderSheetHasDiscountConditionFamily({
      key: sheetKey,
      tabIndex: selectedLineSheetTab?.index ?? -1,
    })
  );

  const handleBulkApplyIva = useCallback(() => {
    if (!selectedLineSheetTab) return;

    const selectedProducts = selectedProductIds
      .map((id) =>
        getInflatedProductById(sheetKey, selectedLineSheetTab.index, id)
      )
      .filter((p): p is NonNullable<typeof p> => p !== undefined);

    const nextProducts = produce(selectedProducts, (draft) => {
      for (const product of draft) {
        // 컨디션이 없는 상품은 IVA 만 넣어도 계산에 쓰이지 않아 건너뛴다.
        if (!product.priceAdjustedBy) continue;
        product.priceAdjustedBy.applyIva = bulkApplyIva;
        product.priceAdjustedBy.ivaValue = bulkIvaValue;
      }
    });
    updateInflatedProducts(sheetKey, selectedLineSheetTab.index, nextProducts);
    setIsDirty(true);
  }, [
    bulkApplyIva,
    bulkIvaValue,
    selectedProductIds,
    sheetKey,
    selectedLineSheetTab,
    getInflatedProductById,
    updateInflatedProducts,
  ]);

  const { isUpdateLineSheetSetQueued } = useLineSheetSetDumbTransaction();

  useEffect(() => {
    return () => {
      resetInflatedSheet(sheetKey);
    };
  }, [sheetKey, resetInflatedSheet]);

  const handleSheetTabFilterClear = useCallback(() => {
    setQuery(
      (prev) => {
        return {
          ...prev,
          ...EMPTY_SHEET_TAB_FILTER_QUERY,
        };
      },
      {
        replace: true,
      }
    );
  }, [setQuery]);

  // 삭제/미발행 등으로 조회가 실패(404/403)하면 빈 화면+토스트 대신 안내 페이지를 보여준다.
  if (
    getLineSheetSetError &&
    isLineSheetSetUnavailableError(getLineSheetSetError)
  ) {
    return <LineSheetNotFoundPage />;
  }

  return (
    <>
      <Helmet>
        <title>Pathrade | Line Sheets</title>
        <link
          rel="canonical"
          href={getCanonicalUrl()}
        />
        <meta property="og:title" content="Pathrade | Line Sheets" />
        <meta
          property="og:url"
          content={getCanonicalUrl()}
        />
      </Helmet>
      <Flex flexDirection={"column"} height={"100%"}>
        <Flex
          flexDirection={"row"}
          p={"8px 16px"}
          gap={"8px"}
          alignItems={"center"}
          borderBottom={"0.5px solid var(--gray, #D4D4D1)"}
          background={"var(--white, #FFF)"}
        >
          <Box
            flexShrink={"0"}
            flexGrow={"0"}
            alignItems={"center"}
            display={"flex"}
          >
            <AppIconButton
              variant={"ghost"}
              ariaLabel={"go to previous page"}
              size={"medium"}
              icon={"left"}
              onClick={() => {
                navigate(-1, "/line-sheets");
              }}
            />
          </Box>
          <LineSheetSetBillboard lineSheetSet={lineSheetSet} />
          <Spacer />
          <Flex flexDirection={"row"} gap={"8px"} wrap={"wrap"} flexGrow={"0"}>
            {company?.type === "AGENCY" &&
              !lineSheetSet?.isDeleted &&
              !lineSheetSet?.publishedAt &&
              lineSheetSet?.id && (
                <AppButton
                  key={"LineSheetSetDetailAction_Edit"}
                  isLoading={isUpdateLineSheetSetQueued({
                    agencyId: identity!.company!.id,
                    lineSheetSetId,
                  })}
                  // isDisabled={props.values.lineSheetOrAttachmentList
                  //   .filter(isLineSheetFormField)
                  //   .some((field) => field.status === "PARSING_PENDING")}
                  onClick={() => {
                    navigateHelper(`/line-sheet-sets/${lineSheetSetId}/edit`, {
                      appendReferer: true,
                    });
                  }}
                  variant={"white"}
                >
                  {tTitle("edit")}
                </AppButton>
              )}
            {company?.type === "AGENCY" && (
              <AppButton
                isLoading={isSaving}
                onClick={handleSave}
                isDisabled={!isDirty}
                disabledMessage={"수정된 값이 없습니다."}
              >
                {tTitle("save")}
              </AppButton>
            )}
            {company?.type === "AGENCY" && !lineSheetSet?.isDeleted && (
              <>
                <AppButton
                  isDisabled={getLineSheetSetData?.publishedAt !== null}
                  width={"210px"}
                  onClick={async () => {
                    const result = await publishLineSheetSetIfConfirmed([
                      lineSheetSetId,
                    ]);
                    if (result) {
                      mutateGetLineSheetSet();
                    }
                  }}
                >
                  {tTitle("line_sheet_sets:publish_this_line_sheet_set")}
                </AppButton>
              </>
            )}
            <AppDownloadButton value={selectedLineSheet?.file} />
          </Flex>
        </Flex>
        <Flex
          flexDirection={"row"}
          p={"8px 16px"}
          gap={"8px"}
          alignItems={"center"}
          borderBottom={"0.5px solid var(--gray, #D4D4D1)"}
          background={"var(--white, #FFF)"}
        >
          <SheetTabFilter
            sheetKey={sheetKey}
            lineSheetSetId={lineSheetSetId}
            lineSheetId={selectedLineSheet?.id!}
            tabIndex={selectedLineSheetTab ? selectedLineSheetTab.index : -1}
            query={query}
            setQuery={setQuery}
            onFilterSelect={handleFilterSelect}
            onClear={handleSheetTabFilterClear}
            queryCount={queryCount}
            setQueryCount={(count: number) => setQueryCount(count)}
            // ID 기반 브랜드 필터 활성화 — 표준명/원본명 모호함 회피.
            brandCompanies={companiesData}
          />
          <Flex
            position={"relative"}
            zIndex={"2"}
            height={"28px"}
            flexDirection={"row"}
            justifyContent={"center"}
            alignItems={"center"}
          >
            <AppSelect<string>
              borderWidth={"0px"}
              isMultiple={false}
              isClearable={false}
              name={tTitle("sort_by")}
              value={query.orderBy}
              options={
                [
                  { name: tTitle("default"), value: "default" },
                  { name: tTitle("price_asc"), value: "price_asc" },
                  { name: tTitle("price_desc"), value: "price_desc" },
                ] as AppSelectOption<string>[]
              }
              onSelect={(value) =>
                setQuery(
                  (prev) => {
                    return {
                      ...prev,
                      orderBy: value,
                    };
                  },
                  {
                    replace: true,
                  }
                )
              }
            />
          </Flex>
        </Flex>
        <Flex>
          <Flex
            position={"relative"}
            zIndex={"2"}
            background={"#FCFCFE"}
            flexDirection={"row"}
            alignItems={"center"}
            width={"652px"}
            gap={"8px"}
            pl={"16px"}
            height={"36px"}
          >
            <Tooltip
              label={
                identity?.company?.type === "AGENCY"
                  ? `#${lineSheetSet?.id} ${lineSheetSet?.name}`
                  : lineSheetSet?.name
              }
              placement={"bottom-start"}
              openDelay={300}
            >
              <Box
                fontSize={"13px"}
                fontWeight={"700"}
                lineHeight={"16px"}
                flexGrow={"1"}
                maxWidth={"300px"}
                overflow={"hidden"}
                textOverflow={"ellipsis"}
                noOfLines={2}
              >
                {identity?.company?.type === "AGENCY"
                  ? `#${lineSheetSet?.id} ${lineSheetSet?.name}`
                  : lineSheetSet?.name}
              </Box>
            </Tooltip>
            <LineSheetSelector
              minWidth={"326px"}
              values={lineSheetSelectValues}
              value={selectedLineSheet}
              onSelect={handleSelectLineSheet}
            />
          </Flex>
          {identity?.company?.type === "AGENCY" && (
            <AppTabSelect
              name={tTitle("line_sheet_tab")}
              value={selectedLineSheetTab}
              values={lineSheetTabSelectValues}
              onSelect={handleSelectLineSheetTab}
            />
          )}
        </Flex>
        <Flex flexGrow={"1"} flexDirection={"column"} minHeight={0}>
          <Flex
            flexDirection={"row"}
            padding={"2px 12px 8px 16px"}
            alignItems={"center"}
            gap={"8px"}
          >
            <Box
              onMouseEnter={() => setIsSheetInfoButtonHovered(true)}
              onMouseLeave={() => setIsSheetInfoButtonHovered(false)}
            >
              <AppButton
                variant={"normal"}
                onClick={handleOrderRulesToggle}
                chakraProps={{
                  p: "4px 8px",
                  border: isSheetInfoButtonHovered
                    ? "0.5px solid #1272EF"
                    : isOrderRulesPanelOpen
                    ? "0.5px solid #00417D"
                    : "0.5px solid #8F8F8C",
                }}
              >
                <Flex gap={"4px"} alignItems={"center"}>
                  <Text
                    textStyle="body-sm-bold"
                    color={
                      isSheetInfoButtonHovered
                        ? "#1272EF"
                        : isOrderRulesPanelOpen
                        ? "#00417D"
                        : "#8F8F8C"
                    }
                  >
                    {tTitle("sheet_info")}
                  </Text>
                  <SVG
                    src={
                      isOrderRulesPanelOpen
                        ? "/icons-v1_1/chevron-double-left.svg"
                        : "/icons-v1_1/chevron-double-right.svg"
                    }
                    width={"16px"}
                    height={"16px"}
                    color={
                      isSheetInfoButtonHovered
                        ? "#1272EF"
                        : isOrderRulesPanelOpen
                        ? "#00417D"
                        : "#8F8F8C"
                    }
                  />
                </Flex>
              </AppButton>
            </Box>
            {/*<AppButton*/}
            {/*  variant={"normal"}*/}
            {/*  onClick={handleOrderRulesToggle}*/}
            {/*  chakraProps={{ p: "4px 8px", border: isOrderRulesPanelOpen ? "0.5px solid #00417D" : "0.5px solid #D4D4D1" }}*/}
            {/*>*/}
            {/*  <Flex gap={"4px"} alignItems={"center"}>*/}
            {/*    <Text*/}
            {/*      textStyle="body-sm-bold"*/}
            {/*      color={isOrderRulesPanelOpen ? "#00417D" : "#444444"}*/}
            {/*    >*/}
            {/*      {tTitle("sheet_info")}*/}
            {/*    </Text>*/}
            {/*    <SVG*/}
            {/*      src={*/}
            {/*        isOrderRulesPanelOpen*/}
            {/*          ? "/icons-v1_1/chevron-double-left.svg"*/}
            {/*          : "/icons-v1_1/chevron-double-right.svg"*/}
            {/*      }*/}
            {/*      width={"16px"}*/}
            {/*      height={"16px"}*/}
            {/*      color={isOrderRulesPanelOpen ? "#00417D" : "#444444"}*/}
            {/*    />*/}
            {/*  </Flex>*/}
            {/*</AppButton>*/}
            <Divider
              orientation={"vertical"}
              marginX={"5px"}
              borderColor={"#D4D4D1"}
            />
            {identity?.company?.type === "AGENCY" && (
              <>
                <PriceAdjustedByTypeSelect
                  width={"96px"}
                  value={bulkBy}
                  isDisabled={selectedProductIds.length === 0}
                  onSelect={(v) => {
                    if (v) setBulkBy(v);
                  }}
                />
                <Input
                  width={"60px"}
                  textAlign={"center"}
                  fontSize={"12px"}
                  type={"text"}
                  h={"28px"}
                  minH={"0"}
                  p={"0 4px"}
                  borderColor={"#D4D4D1"}
                  value={bulkAmount}
                  isDisabled={selectedProductIds.length === 0}
                  onChange={(e) => setBulkAmount(e.target.value)}
                />
                <Box fontSize={"12px"}>%</Box>
                <AppButton
                  isDisabled={selectedProductIds.length === 0}
                  onClick={handleBulkApply}
                >
                  {tTitle("apply")}
                </AppButton>
                {/* IVA 는 컨디션과 별도로 적용 — 기존 by/value 는 유지하고 IVA 만 변경 */}
                {hasDiscountCondition && (
                  <>
                    <Divider
                      orientation={"vertical"}
                      marginX={"5px"}
                      borderColor={"#D4D4D1"}
                    />
                    <Checkbox
                      isChecked={bulkApplyIva}
                      isDisabled={selectedProductIds.length === 0}
                      onChange={(e) => setBulkApplyIva(e.target.checked)}
                    >
                      <Box fontSize={"12px"}>IVA</Box>
                    </Checkbox>
                    <Input
                      width={"72px"}
                      textAlign={"center"}
                      fontSize={"12px"}
                      type={"text"}
                      h={"28px"}
                      minH={"0"}
                      p={"0 6px"}
                      placeholder={"/1.22"}
                      borderColor={"#D4D4D1"}
                      value={bulkIvaValue}
                      isDisabled={
                        selectedProductIds.length === 0 || !bulkApplyIva
                      }
                      onChange={(e) => setBulkIvaValue(e.target.value)}
                    />
                    <AppButton
                      isDisabled={selectedProductIds.length === 0}
                      onClick={handleBulkApplyIva}
                    >
                      {`IVA ${tTitle("apply")}`}
                    </AppButton>
                  </>
                )}
                <Divider
                  orientation={"vertical"}
                  marginX={"5px"}
                  borderColor={"#D4D4D1"}
                />
              </>
            )}
            {identity?.company?.type === "AGENCY" && (
              <>
                <AppButton
                  variant={"normal"}
                  onClick={() => {
                    openDialog({
                      title: "",
                      message: "정말 라인시트를 정규화하시겠습니까?",
                      confirmName: "정규화",
                    }).then((result) => {
                      if (!result) {
                        return;
                      }
                      normalizeLineSheet(
                        {
                          agencyId: identity?.company?.id || -1,
                          lineSheetSetId: lineSheetSetId,
                          lineSheetId: selectedLineSheet?.id || -1,
                        },
                        {}
                      );
                    });
                  }}
                >
                  {tTitle("common:normalize_line_sheet")}
                </AppButton>
                <AppButton
                  variant={"normal"}
                  onClick={() => {
                    openDialog({
                      title: "",
                      message:
                        "정말 원본 이미지를 1개만 남기고 나머지는 제거하시겠습니까?",
                      confirmName: "제거",
                    }).then((result) => {
                      if (!result) {
                        return;
                      }
                      const res = cleanLineSheetImage(
                        {
                          agencyId: identity?.company?.id || -1,
                          lineSheetSetId: lineSheetSetId,
                          lineSheetId: selectedLineSheet?.id || -1,
                        },
                        {
                          deleteAll: false,
                        }
                      );
                      res.then(() => {
                        mutateListAllLineSheetTabs();
                      });
                    });
                  }}
                >
                  {tTitle("common:clean_org_img")}
                </AppButton>
                <AppButton
                  variant={"normal"}
                  onClick={() => {
                    openDialog({
                      title: "",
                      message: "정말 원본 이미지를 전부 제거하시겠습니까?",
                      confirmName: "제거",
                    }).then((result) => {
                      if (!result) {
                        return;
                      }
                      const res = cleanLineSheetImage(
                        {
                          agencyId: identity?.company?.id || -1,
                          lineSheetSetId: lineSheetSetId,
                          lineSheetId: selectedLineSheet?.id || -1,
                        },
                        {
                          deleteAll: true,
                        }
                      );
                      res.then(() => {
                        mutateListAllLineSheetTabs();
                      });
                    });
                  }}
                >
                  {tTitle("common:clean_org_img_all")}
                </AppButton>
                <AppButton
                  width={"210px"}
                  isDisabled={selectedLineSheetTab?.pdfFile?.id === undefined}
                  onClick={async () => {
                    if (!selectedLineSheetTab) {
                      return;
                    }
                    // const imageUrl = `https://pathrade-prod.s3.ap-northeast-2.amazonaws.com/line-sheet-sets/${lineSheetSetId}/line-sheets/${selectedLineSheet?.id}/preview/${selectedLineSheetTab.name}/preview_top.png`;
                    // openImageWindow(imageUrl);
                    const pdfUrl = `https://pathrade-prod.s3.ap-northeast-2.amazonaws.com/line-sheet-sets/${lineSheetSetId}/line-sheets/${selectedLineSheet?.id}/preview/${selectedLineSheetTab.name}/preview.pdf`;
                    openPdfWindow(pdfUrl);
                  }}
                >
                  {tTitle("line_sheet_sets:preview_excel")}
                </AppButton>
              </>
            )}
          </Flex>
          <Flex flexGrow={"1"} flexDirection={"row"} gap={"8px"} minHeight={0}>
            {isOrderRulesPanelOpen && (
              <Box height={"100%"} minHeight={0}>
                <SheetInfoPanel
                  onClose={handleOrderRulesToggle}
                  lineSheetSet={lineSheetSet}
                />
              </Box>
            )}
            <LineSheetTab
              lineSheetSetId={lineSheetSetId}
              lineSheetId={selectedLineSheet?.id || -1}
              tabIndex={selectedLineSheetTab ? selectedLineSheetTab.index : -1}
              isLoading={false}
              onSelectedProductIdsChange={setSelectedProductIds}
              onGridReady={handleGridReady}
              mutate={mutateListAllLineSheetTabs}
              onDirtyChange={setIsDirty}
            />
          </Flex>
        </Flex>
      </Flex>
    </>
  );
}
